import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from "./Navbar";
import api from '../utils/api';

const SuperadminLoginPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    let errs = {};
    if (!formData.username.trim()) errs.username = 'Username or Email is required';
    if (!formData.password) errs.password = 'Password is required';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/superadmin/login', formData);
      const data = response.data;
      if (data.success && data.user) {
        localStorage.setItem('superadmin_user', JSON.stringify(data.user));
        navigate('/superadmin');
      } else {
        setErrors({ submit: 'Authentication failed.' });
      }
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.message || 'Error logging in';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.formBox}>
            <div style={styles.badge}>SYSTEM PORTAL</div>
            <h2 style={styles.title}>Superadmin Access</h2>
            <p style={styles.subtitle}>Enter credentials to access the master control panel</p>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Username or Email</label>
                <input
                  type="text"
                  name="username"
                  placeholder="admin"
                  value={formData.username}
                  onChange={handleChange}
                  style={styles.input}
                />
                {errors.username && <p style={styles.fieldError}>{errors.username}</p>}
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Security Password</label>
                <div style={{ position: 'relative', display: 'flex', width: '100%' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="••••••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    style={{...styles.input, paddingRight: '36px'}}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      color: 'var(--text-muted)',
                      transition: 'color 0.15s ease',
                      zIndex: 2,
                    }}
                  >
                    {showPassword ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && <p style={styles.fieldError}>{errors.password}</p>}
              </div>

              {errors.submit && <p style={styles.error}>{errors.submit}</p>}

              <button type="submit" style={styles.button} disabled={loading}>
                {loading ? 'Authenticating...' : 'Sign In to Console'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  page: {
    height: 'calc(100vh - 78px)',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-page)',
    padding: '20px 16px',
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
  },

  container: {
    width: '100%',
    maxWidth: '440px',
    borderRadius: '24px',
    background: 'var(--bg-card)',
    backdropFilter: 'blur(16px)',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow-lg)',
    padding: '40px 32px',
    boxSizing: 'border-box',
  },

  formBox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  badge: {
    background: 'var(--bg-surface-alt)',
    color: 'var(--text-secondary)',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '1.5px',
    padding: '6px 12px',
    borderRadius: '100px',
    marginBottom: '16px',
    textTransform: 'uppercase',
    border: '1px solid var(--border-color)',
  },

  title: {
    fontSize: '26px',
    fontWeight: '800',
    marginBottom: '8px',
    color: 'var(--text-primary)',
    textAlign: 'center',
    letterSpacing: '-0.5px',
  },

  subtitle: {
    color: 'var(--text-tertiary)',
    marginBottom: '32px',
    fontSize: '14px',
    textAlign: 'center',
    lineHeight: '1.5',
  },

  form: {
    width: '100%',
  },

  inputGroup: {
    marginBottom: '20px',
    width: '100%',
  },

  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
    marginBottom: '8px',
  },

  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    border: '1px solid var(--border-input)',
    fontSize: '14px',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, background-color 0.2s',
    outline: 'none',
  },

  fieldError: {
    color: '#f87171',
    fontSize: '12px',
    marginTop: '6px',
    marginBottom: '0',
  },

  error: {
    color: '#ef4444',
    fontSize: '13px',
    fontWeight: '500',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid rgba(239, 68, 68, 0.2)',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
  },

  button: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '15px',
    marginTop: '10px',
    boxShadow: '0 4px 20px rgba(29, 78, 216, 0.25)',
    transition: 'transform 0.1s, opacity 0.2s',
  },
};

export default SuperadminLoginPage;
